import Joi from 'joi';
import api from '../../api';
import { contentTranslationSchema, contentTranslationDataVersionSchema, paginationSchema } from '../schemas';

/**
 * Get a content translation by its ID.
 * @param {string} contentTranslationId
 * @returns {Promise<ContentTranslation>}
 */
export const getContentTranslation = async (contentTranslationId) => {
  const result = await api.get(`/content-management/v1-0/admin/content-translations/${contentTranslationId}`);

  try {
    await contentTranslationSchema.validateAsync(result.data, { allowUnknown: true });
  } catch (error) {
    throw new Error(`API response do not match Content translation Schema : ${error.message}`);
  }

  return result.data;
};
/**
 * Get all content translations data version
 * @param {string} contentTranslationId
 * @param {object} queryParams
 * @param {number} queryParams.pageNumber
 * @param {number} queryParams.pageSize
 * @returns {Promise<{data: ContentTranslationDataVersion[], pagination: Pagination}>}
 */
export const getAllContentTranslationsDataVersion = async (contentTranslationId, queryParams) => {
  const result = await api.get(`/content-management/v1-0/admin/content-translations/${contentTranslationId}/data-versions`, { params: queryParams });

  try {
    const schema = Joi.object({
      data: Joi.array().items(contentTranslationDataVersionSchema).required(),
      pagination: paginationSchema,
    });
    await schema.validateAsync(result.data, { allowUnknown: true });
  } catch (error) {
    throw new Error(`API response do not match Content Translation Data Version Schema : ${error.message}`);
  }

  return result.data;
};
/**
 * Update a Content translation
 * @param {string} contentTranslationId
 * @param {object} updatedContentTranslation
 * @param {object} updatedContentTranslation.data
 * @param {number} version
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const updateContentTranslation = async (
  contentTranslationId,
  updatedContentTranslation,
  version,
) => api.patch(
  `/content-management/v1-0/admin/content-translations/${contentTranslationId}`,
  updatedContentTranslation,
  {
    headers: {
      'X-Presumed-Version': version,
    },
  },
);
/**
 * Create a new Content translation
 * @param {object} newContentTranslation
 * @param {string} newContentTranslation.contentId
 * @param {string} newContentTranslation.localeId
 * @param {object} newContentTranslation.data
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postContentTranslation = async (newContentTranslation) => api.post('/content-management/v1-0/admin/contents', newContentTranslation);
/**
 * Publish a Content translation
 * @param {string} contentTranslationId
 * @param {number} version
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postPublishContent = async (contentTranslationId, version) => api.post(
  `/content-management/v1-0/admin/content-translations/${contentTranslationId}:publish`,
  undefined,
  {
    headers: {
      'X-Presumed-Version': version,
    },
  },
);
/**
 * Withdraw a Content Translation
 * @param {string} contentTranslationId
 * @param {number} version
 * @returns {Promise<import("axios").AxiosResponse>}
 */
export const postWithdrawContent = async (contentTranslationId, version) => api.post(
  `/content-management/v1-0/admin/content-translations/${contentTranslationId}:withdraw`,
  undefined,
  {
    headers: {
      'X-Presumed-Version': version,
    },
  },
);
